import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import useAuthStore from '../../lib/auth-store';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '../../components/ui/dropdown-menu';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
  Users, Plus, Search, MoreHorizontal, UserPlus, Pause, XCircle,
  KeyRound, MailPlus, RefreshCw, Copy, Check, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import CreateUserWizard from '../../components/team/CreateUserWizard';

const statusColors = {
  active: 'bg-green-100 text-green-700 border-green-200',
  invited: 'bg-blue-100 text-blue-700 border-blue-200',
  suspended: 'bg-red-100 text-red-700 border-red-200',
  deactivated: 'bg-gray-100 text-gray-600 border-gray-200',
};

const roleLabels = {
  super_admin: 'Super Admin',
  iso: 'ISO',
  iso_agent: 'ISO Agent',
  iso_subagent: 'ISO Subagent',
};

export default function UserManagement() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { startImpersonation } = useAuthStore();
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tenantFilter, setTenantFilter] = useState('all');

  // Create/Invite dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create' | 'invite'
  const [form, setForm] = useState({ tenant_id: '', first_name: '', last_name: '', email: '', phone: '', role: 'iso_agent', password: '' });
  const [saving, setSaving] = useState(false);

  // Temp password dialog
  const [tempPwdDialog, setTempPwdDialog] = useState(false);
  const [tempPassword, setTempPassword] = useState('');
  const [copied, setCopied] = useState(false);

  // Wizard
  const [wizardOpen, setWizardOpen] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [usersData, tenantsData] = await Promise.all([api.getAdminUsers(), api.getTenants()]);
      setUsers(usersData);
      setTenants(tenantsData);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const openCreateDialog = () => {
    setDialogMode('create');
    setForm({ tenant_id: tenants[0]?.id || '', first_name: '', last_name: '', email: '', phone: '', role: 'iso_agent', password: '' });
    setDialogOpen(true);
  };

  const openInviteDialog = useCallback(() => {
    setDialogMode('invite');
    setForm({ tenant_id: tenants[0]?.id || '', first_name: '', last_name: '', email: '', role: 'iso_agent' });
    setDialogOpen(true);
  }, [tenants]);

  // Re-filter when search/filter params or users change
  useEffect(() => {
    let list = users;
    if (roleFilter !== 'all') list = list.filter((u) => u.role === roleFilter);
    if (statusFilter !== 'all') list = list.filter((u) => u.status === statusFilter);
    if (tenantFilter !== 'all') list = list.filter((u) => u.tenant_id === tenantFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((u) =>
        `${u.first_name} ${u.last_name}`.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q) ||
        u.tenant_name?.toLowerCase().includes(q)
      );
    }
    setFiltered(list);
  }, [users, search, roleFilter, statusFilter, tenantFilter]);

  // Open invite dialog if redirected with ?action=invite
  useEffect(() => {
    if (searchParams.get('action') === 'invite') openInviteDialog();
  }, [searchParams, openInviteDialog]);

  const handleSave = async () => {
    if (!form.email.trim() || !form.first_name.trim()) { toast.error('Name and email are required'); return; }
    if (!form.tenant_id) { toast.error('Please select a tenant'); return; }
    setSaving(true);
    try {
      if (dialogMode === 'invite') {
        const result = await api.inviteAdminUser({
          tenant_id: form.tenant_id, first_name: form.first_name,
          last_name: form.last_name, email: form.email, role: form.role,
        });
        toast.success(`Invitation sent to ${result.email}`);
      } else {
        const result = await api.createAdminUser({
          tenant_id: form.tenant_id, first_name: form.first_name,
          last_name: form.last_name, email: form.email, phone: form.phone,
          role: form.role, password: form.password || undefined,
        });
        if (result.temp_password) {
          setTempPassword(result.temp_password);
          setTempPwdDialog(true);
        }
        toast.success(`User ${result.email} created`);
      }
      setDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.message || 'Operation failed');
    } finally {
      setSaving(false);
    }
  };

  const handleAction = async (userId, action) => {
    try {
      if (action === 'suspend') {
        await api.suspendUser(userId);
        toast.success('User suspended');
      } else if (action === 'deactivate') {
        await api.deactivateUser(userId);
        toast.success('User deactivated');
      } else if (action === 'reset-password') {
        const result = await api.resetUserPassword(userId);
        setTempPassword(result.temp_password);
        setTempPwdDialog(true);
        toast.success('Password reset');
      } else if (action === 'resend-invite') {
        await api.resendUserInvite(userId);
        toast.success('Invite resent');
      } else if (action === 'impersonate') {
        const result = await api.impersonateUser(userId);
        if (result.access_token) {
          await startImpersonation(result.access_token);
          navigate('/');
          toast.success('Now viewing as this user');
        }
      }
      loadData();
    } catch (err) {
      toast.error(err.message || 'Action failed');
    }
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(tempPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="user-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">User Management</h1>
          <p className="text-sm text-muted-foreground mt-1">{users.length} users across all tenants</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="icon" onClick={loadData} data-testid="refresh-users-btn">
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button variant="outline" onClick={openInviteDialog} data-testid="invite-user-btn">
            <MailPlus className="h-4 w-4 mr-2" /> Invite User
          </Button>
          <Button className="bg-[#3b82f6] hover:bg-[#2563eb] text-white" onClick={() => setWizardOpen(true)} data-testid="create-user-btn">
            <UserPlus className="h-4 w-4 mr-2" /> Create User
          </Button>
        </div>
      </div>

      <CreateUserWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onCreated={() => { loadData(); }}
      />

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search users..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="search-users-input" />
          </div>
          <Select value={tenantFilter} onValueChange={setTenantFilter}>
            <SelectTrigger className="w-[180px]" data-testid="filter-tenant-select">
              <SelectValue placeholder="All Tenants" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Tenants</SelectItem>
              {tenants.map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.company_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-[150px]" data-testid="filter-role-select">
              <SelectValue placeholder="All Roles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              {Object.entries(roleLabels).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px]" data-testid="filter-status-select">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="invited">Invited</SelectItem>
              <SelectItem value="suspended">Suspended</SelectItem>
              <SelectItem value="deactivated">Deactivated</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="users-table">
            <thead>
              <tr className="border-b text-left text-sm text-muted-foreground">
                <th className="px-6 py-3 font-medium">User</th>
                <th className="px-6 py-3 font-medium">Tenant</th>
                <th className="px-6 py-3 font-medium">Role</th>
                <th className="px-6 py-3 font-medium">Status</th>
                <th className="px-6 py-3 font-medium">Last Login</th>
                <th className="px-6 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="text-center py-12 text-muted-foreground">No users found</td></tr>
              )}
              {filtered.map((u) => {
                const initials = `${(u.first_name || u.full_name || '')[0] || ''}${(u.last_name || '')[0] || ''}`.toUpperCase() || 'U';
                const name = u.first_name ? `${u.first_name} ${u.last_name || ''}`.trim() : u.full_name || u.email;
                return (
                  <tr key={u.id} className="border-b last:border-0 hover:bg-accent/50 transition-colors" data-testid={`user-row-${u.id}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-primary/10 rounded-full flex items-center justify-center text-xs font-semibold text-primary">
                          {initials}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{name}</p>
                          <p className="text-xs text-muted-foreground">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm">{u.tenant_name || '-'}</td>
                    <td className="px-6 py-4">
                      <span className="text-xs font-medium px-2 py-1 bg-accent rounded">{roleLabels[u.role] || u.role}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusColors[u.status] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                        {u.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">
                      {u.last_login_at ? new Date(u.last_login_at).toLocaleDateString() : 'Never'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`user-actions-${u.id}`}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/admin/users/${u.id}`)} data-testid={`manage-user-${u.id}`}>
                            <UserPlus className="h-4 w-4 mr-2" /> Manage / Residual Setup
                          </DropdownMenuItem>
                          {['iso', 'iso_agent', 'iso_subagent'].includes(u.role) && (
                            <DropdownMenuItem onClick={() => navigate(`/team/${u.id}`)} data-testid={`team-economics-${u.id}`}>
                              <UserPlus className="h-4 w-4 mr-2" /> Team Economics
                            </DropdownMenuItem>
                          )}
                          {u.status === 'active' && u.role !== 'super_admin' && (
                            <DropdownMenuItem onClick={() => handleAction(u.id, 'impersonate')} data-testid={`impersonate-user-${u.id}`}>
                              <Eye className="h-4 w-4 mr-2" /> Login As
                            </DropdownMenuItem>
                          )}
                          {u.status === 'active' && (
                            <DropdownMenuItem onClick={() => handleAction(u.id, 'suspend')} className="text-red-600" data-testid={`suspend-user-${u.id}`}>
                              <Pause className="h-4 w-4 mr-2" /> Suspend
                            </DropdownMenuItem>
                          )}
                          {(u.status === 'active' || u.status === 'suspended') && (
                            <DropdownMenuItem onClick={() => handleAction(u.id, 'deactivate')} data-testid={`deactivate-user-${u.id}`}>
                              <XCircle className="h-4 w-4 mr-2" /> Deactivate
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          {u.status === 'active' && (
                            <DropdownMenuItem onClick={() => handleAction(u.id, 'reset-password')} data-testid={`reset-pwd-user-${u.id}`}>
                              <KeyRound className="h-4 w-4 mr-2" /> Reset Password
                            </DropdownMenuItem>
                          )}
                          {u.status === 'invited' && (
                            <DropdownMenuItem onClick={() => handleAction(u.id, 'resend-invite')} data-testid={`resend-invite-${u.id}`}>
                              <MailPlus className="h-4 w-4 mr-2" /> Resend Invite
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Create/Invite Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{dialogMode === 'invite' ? 'Invite User' : 'Create User'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Tenant *</Label>
              <Select value={form.tenant_id} onValueChange={(v) => setField('tenant_id', v)}>
                <SelectTrigger className="mt-1" data-testid="user-form-tenant-select">
                  <SelectValue placeholder="Select tenant" />
                </SelectTrigger>
                <SelectContent>
                  {tenants.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.company_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>First Name *</Label>
                <Input value={form.first_name} onChange={(e) => setField('first_name', e.target.value)} className="mt-1" data-testid="user-form-first-name" />
              </div>
              <div>
                <Label>Last Name *</Label>
                <Input value={form.last_name} onChange={(e) => setField('last_name', e.target.value)} className="mt-1" data-testid="user-form-last-name" />
              </div>
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} className="mt-1" data-testid="user-form-email" />
            </div>
            <div>
              <Label>Role *</Label>
              <Select value={form.role} onValueChange={(v) => setField('role', v)}>
                <SelectTrigger className="mt-1" data-testid="user-form-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="iso">ISO</SelectItem>
                  <SelectItem value="iso_agent">ISO Agent</SelectItem>
                  <SelectItem value="iso_subagent">ISO Subagent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {dialogMode === 'create' && (
              <>
                <div>
                  <Label>Phone</Label>
                  <Input value={form.phone || ''} onChange={(e) => setField('phone', e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label>Password (leave blank for auto-generated)</Label>
                  <Input type="password" value={form.password || ''} onChange={(e) => setField('password', e.target.value)} className="mt-1" data-testid="user-form-password" />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-[#3b82f6] hover:bg-[#2563eb] text-white" onClick={handleSave} disabled={saving} data-testid="save-user-btn">
              {saving ? 'Saving...' : dialogMode === 'invite' ? 'Send Invite' : 'Create User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Temp Password Dialog */}
      <Dialog open={tempPwdDialog} onOpenChange={setTempPwdDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Temporary Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">Share this password securely with the user. They will be required to change it on first login.</p>
            <div className="flex items-center gap-2">
              <Input value={tempPassword} readOnly className="font-mono" data-testid="temp-password-value" />
              <Button variant="outline" size="icon" onClick={copyPassword} data-testid="copy-temp-password-btn">
                {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setTempPwdDialog(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
